/**
 * The six Account settings pages, as data: their `?tab=` values and titles.
 *
 * Split out of `SettingsScreen` so `validateSearch` can import the tab list
 * without the route file importing itself. V1 also fed the old nine-tab
 * `AccountTabs` strip from this file; that strip is gone (the sidebar's
 * ACCOUNT group replaced it — see `AccountPageHeader.tsx`), but the six
 * `SettingsTab` values and their titles are still exactly what `?tab=` is
 * validated against and what each page's `TitleRow` reads, so this file stays.
 *
 * ## Why the six settings pages are one route with a `?tab=`
 *
 * V2 draws nine tabs, and seven of them are pages this app has no route for:
 * `/settings/security`, `/settings/privacy` and friends. Those routes are
 * declared in `src/routes/app.ts`, which this family does not own — six other
 * families are editing the repo at the same time and a shared route manifest is
 * exactly the file that produces a merge conflict nobody asked for.
 *
 * So the six pages are states of the existing `/settings` route, addressed by
 * a validated search param. Every one of them is still a real URL that can be
 * shared, deep-linked, and reached with the back button, which is what the tab
 * strip needs from them. Promoting them to path routes later is a change to the
 * manifest and to `DEFAULT_SETTINGS_TAB`, and nothing else — see the report.
 */

export const SETTINGS_TABS = [
  'edit-profile',
  'notifications',
  'appearance',
  'security',
  'privacy',
  'help',
] as const

export type SettingsTab = (typeof SETTINGS_TABS)[number]

/**
 * Where a bare `/settings` lands.
 *
 * V2 has no "Settings" index — the Account hub IS the index, and every row on
 * it deep-links to one of these pages. Nothing in the app links to `/settings`
 * without a tab; the route is kept so an old bookmark still opens something
 * rather than 404ing, and the first tab is the least surprising thing to open.
 */
export const DEFAULT_SETTINGS_TAB: SettingsTab = 'edit-profile'

/** Sentence case (owner C7): the page's own name, as the hub row that opens it says it. */
const TAB_TITLES: Record<SettingsTab, string> = {
  'edit-profile': 'Edit profile',
  notifications: 'Notifications',
  appearance: 'Appearance and language',
  security: 'Sign in and devices',
  privacy: 'Data and privacy',
  help: 'Help and support',
}

/** The page's own h1. Longer than the tab label for three of the six. */
export const settingsTitle = (tab: SettingsTab): string => TAB_TITLES[tab]

const isSettingsTab = (value: unknown): value is SettingsTab =>
  SETTINGS_TABS.some((tab) => tab === value)

/**
 * `?tab=` → a tab, falling back rather than throwing: a hand-typed or stale
 * URL should open the page, not drop the member on a router error boundary.
 */
export const parseSettingsTab = (value: unknown): SettingsTab =>
  isSettingsTab(value) ? value : DEFAULT_SETTINGS_TAB
